import React from 'react';
import { CircleCheckIcon } from '@/assets/icons/CircleCheckIcon';

import * as state from '@/components/Analysis/state';
import { ReportInternalType, UnitSystemEnum } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { formatEnumForDisplay, titleCaseSentence, upperSnakeCase } from '@/utils';
import { ArrowDropDown } from '@mui/icons-material';
import {
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { AccordionEditButton } from '@/components/shared/Accordion/AccordionEditButton';
import { useCallback } from 'react';
import { ReportName } from '../ReportsLanding/styles';
import { useReportSummary } from '../hooks';
import { ExportPoiReportButton } from './ExportReport';
import { ReportMenu } from './ReportMenu';
import { ReportUpdator } from './ReportStatusUpdator';
import {
  ReportDetailsDocument,
  ReportActionStatus,
  useUpdateReportActionStatusMutation,
  useUpdateReportDueDateMutation,
  useUpdateReportMutation,
} from '@/__generated__/graphql';
import {
  Divider,
  EndAdormentContainer,
  Header,
  InputLabel,
  LoaderContainer,
  ReportDatePicker,
  ReportSummaryLabel,
  ReportSummaryValue,
  StatusContainer,
  StatusLabel,
  StyledDropDownIcon,
  SummaryLineBreak,
  datePickerSlotProps,
} from './styles';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { ExportPaintBlockReportButton } from './PaintBlockReport/PaintBlockReportPdf/ExportPaintBlockReportButton';
import { ReportHeaderProps } from './ReportContainers/types';
import { useAtom, useAtomValue } from 'jotai';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

const actionStatuses = [
  ReportActionStatus.Backlog,
  ReportActionStatus.InProgress,
  ReportActionStatus.Complete,
];

const InputLoader = () => {
  return (
    <LoaderContainer>
      <CircularProgress
        size={20}
        sx={{
          color: abyssColors.primary[500],
        }}
      />
    </LoaderContainer>
  );
};

interface EditNameState {
  isEditing: boolean;
  value: string;
}

export const ReportHeader = ({
  blockOverViewData,
  chaseItemsData,
  actualValuesData,
  remarksData,
}: ReportHeaderProps) => {
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const summaryData = useReportSummary();
  const [editNameState, setEditNameState] = React.useState<EditNameState>({
    isEditing: false,
    value: summaryData?.name || '',
  });

  const [updateReport, { loading: loadingNameUpdate, error: errorNameUpdate }] =
    useUpdateReportMutation({
      refetchQueries: [ReportDetailsDocument],
      onCompleted: (response) => {
        if (!response?.updateReport) {
          showSnackBar('Something went wrong', 'error');
          return;
        }
        showSnackBar('Report name updated successfully', 'success');
        if (selectedReport) {
          setSelectedReport({
            ...selectedReport,
            name: editNameState.value,
          });
        }
        setEditNameState({ isEditing: false, value: editNameState.value });
      },
      onError: (errorResponse: Error) => {
        showSnackBar(errorResponse.message, 'error');
      },
    });

  const [selectedReport, setSelectedReport] = useAtom(state.selectedReport);
  const unitConversionSystem = useAtomValue(state.unitSystem);

  const [mutateStatus, { loading: loadingStatusUpdate, error: errorStatusUpdate }] =
    useUpdateReportActionStatusMutation({
      refetchQueries: [ReportDetailsDocument],
      onCompleted: (response) => {
        if (!response?.updateReport) {
          showSnackBar('Something went wrong', 'error');
          return;
        }
        showSnackBar('Action status updated successfully', 'success');
        if (selectedReport) {
          setSelectedReport({
            ...selectedReport,
            actionStatus: response?.updateReport?.actionStatus,
          });
        }
      },
      onError: (errorResponse) => {
        showSnackBar(errorResponse.message, 'error');
      },
    });

  const [mutateDueDate, { loading: loadingDueDateUpdate, error: errorDueDateUpdate }] =
    useUpdateReportDueDateMutation({
      refetchQueries: [ReportDetailsDocument],
      onCompleted: (response) => {
        if (!response?.updateReport) {
          showSnackBar('Something went wrong', 'error');
          return;
        }
        showSnackBar('Report due date updated successfully', 'success');
        if (selectedReport) {
          setSelectedReport({
            ...selectedReport,
            dueDate: response?.updateReport?.dueDate,
          });
        }
      },
      onError: (errorResponse) => {
        showSnackBar(errorResponse.message, 'error');
      },
    });

  const handleStatusChange = useCallback(
    (event: SelectChangeEvent) => {
      const newValue = event.target.value;
      if (!newValue) {
        return;
      }
      const updatedStatus = Object.values(ReportActionStatus).find(
        (actionStatus) => actionStatus === upperSnakeCase(newValue)
      );
      if (updatedStatus && selectedReport?.id) {
        mutateStatus({
          variables: {
            input: {
              reportId: selectedReport?.id,
              actionStatus: updatedStatus,
            },
          },
        });
      }
    },
    [mutateStatus, selectedReport?.id]
  );

  const renderStatus = useCallback((value: string) => {
    return (
      <StatusContainer>
        <Stack direction="row" alignItems="center">
          {value === ReportActionStatus.Complete && (
            <CircleCheckIcon style={{ marginRight: '6px' }} />
          )}
          <StatusLabel>{formatEnumForDisplay(value)}</StatusLabel>
        </Stack>
        <EndAdormentContainer>
          <Divider />
          <StyledDropDownIcon />
        </EndAdormentContainer>
      </StatusContainer>
    );
  }, []);

  const getPaintBlockReportData = useCallback(() => {
    return (
      <ExportPaintBlockReportButton
        blockOverViewData={blockOverViewData}
        chaseItemsData={chaseItemsData}
        actualValuesData={actualValuesData}
        remarksData={remarksData}
      />
    );
  }, [actualValuesData, blockOverViewData, chaseItemsData, remarksData]);

  return (
    <Header>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          {editNameState.isEditing ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                value={editNameState.value}
                onChange={(e) => setEditNameState((prev) => ({ ...prev, value: e.target.value }))}
                autoFocus
                sx={{
                  minWidth: '50px',
                  maxWidth: '300px',
                  '& .MuiInputBase-input': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
              {loadingNameUpdate && !errorNameUpdate && <InputLoader />}
              {!loadingNameUpdate && (
                <AccordionEditButton
                  title="Report Name"
                  isEditing={editNameState.isEditing}
                  onEdit={() => {}}
                  onSave={() => {
                    if (selectedReport?.id) {
                      updateReport({
                        variables: {
                          input: {
                            reportId: selectedReport.id,
                            name: editNameState.value,
                          },
                        },
                      });
                    }
                  }}
                  onCancel={() =>
                    setEditNameState({ isEditing: false, value: summaryData?.name || '' })
                  }
                />
              )}
            </Stack>
          ) : (
            <>
              <ReportName
                data-testid="report-name"
                sx={{
                  minWidth: '50px',
                  maxWidth: '300px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              >
                {summaryData?.name}
              </ReportName>
              <AccordionEditButton
                title="Report Name"
                isEditing={editNameState.isEditing}
                onEdit={() => setEditNameState({ isEditing: true, value: summaryData?.name || '' })}
                onSave={() => {}}
                onCancel={() => {}}
              />
              {loadingNameUpdate && !errorNameUpdate && <InputLoader />}
            </>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={4}>
          <Stack direction="row" alignItems="center" gap={3}>
            <Stack direction="row" alignItems="center">
              <InputLabel data-testid="due-date-picker-label">Due Date</InputLabel>
              {loadingDueDateUpdate && !errorDueDateUpdate && <InputLoader />}
              {!loadingDueDateUpdate && (
                <ReportDatePicker
                  format="dd MMM yyyy"
                  disablePast
                  slotProps={datePickerSlotProps}
                  value={selectedReport?.dueDate ? new Date(selectedReport?.dueDate) : undefined}
                  onChange={(value) => {
                    if (selectedReport?.id && value) {
                      mutateDueDate({
                        variables: {
                          input: {
                            reportId: selectedReport?.id,
                            dueDate: value.toString(),
                          },
                        },
                      });
                    }
                  }}
                  slots={{
                    openPickerIcon: ArrowDropDown,
                  }}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center">
              <InputLabel>Status</InputLabel>
              {loadingStatusUpdate && !errorStatusUpdate && <InputLoader />}
              {!loadingStatusUpdate && (
                <Select
                  data-testid="action-status-select"
                  variant="outlined"
                  // TODO: Figure out how to add placeholders in MUI 6
                  // placeholder="Update status"
                  renderValue={renderStatus}
                  onChange={handleStatusChange}
                  value={selectedReport?.actionStatus ?? ''}
                  IconComponent={ArrowDropDown}
                  disabled={loadingStatusUpdate}
                  sx={{
                    width: '65%',
                    '& .MuiSelect-select': {
                      padding: '0px !important',
                      alignItems: 'center',
                      height: '26px',
                      display: 'flex',
                    },
                    '& .MuiSelect-iconOutlined': {
                      display: 'none',
                    },
                    '& fieldset': {
                      borderColor: abyssColors.primary[500],
                    },
                    borderRadius: '4px',
                    padding: 0,
                  }}
                >
                  {actionStatuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      {formatEnumForDisplay(option)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Stack>
          </Stack>
          <Stack gap={1} direction="row" alignItems="center">
            <ReportUpdator />
            {selectedReport?.internalType === ReportInternalType.PaintBlock ? (
              getPaintBlockReportData()
            ) : (
              <ExportPoiReportButton />
            )}
            <ReportMenu />
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          {selectedReport?.internalType === 'PAINT_BLOCK' ? (
            <>
              <ReportSummaryLabel>Block Name: </ReportSummaryLabel>
              <ReportSummaryValue>{selectedReport?.paintRegion?.name}</ReportSummaryValue>
              <SummaryLineBreak />
              <ReportSummaryLabel>Type: </ReportSummaryLabel>
              <ReportSummaryValue>{titleCaseSentence(selectedReport.type)}</ReportSummaryValue>
            </>
          ) : (
            <>
              <ReportSummaryLabel>
                Total area in square{' '}
                {unitConversionSystem === UnitSystemEnum.Imperial ? 'feet' : 'meters'}:
              </ReportSummaryLabel>
              <ReportSummaryValue>{summaryData.area}</ReportSummaryValue>
              <SummaryLineBreak />
              <ReportSummaryLabel>Equipments:</ReportSummaryLabel>
              <ReportSummaryValue>{summaryData.equipments}</ReportSummaryValue>
              <SummaryLineBreak />
              <ReportSummaryLabel>Points of interest:</ReportSummaryLabel>
              <ReportSummaryValue>{summaryData.pois}</ReportSummaryValue>
            </>
          )}
        </Stack>
        <Stack direction="row" alignItems="center">
          <ReportSummaryLabel>Created by:</ReportSummaryLabel>
          <UserAvatar
            name={selectedReport?.createdByUser?.name}
            picture={selectedReport?.createdByUser?.picture}
            sx={{ height: '24px', width: '24px', mr: 0.5 }}
          />
          <ReportSummaryValue>{selectedReport?.createdByUser.name}</ReportSummaryValue>
        </Stack>
      </Stack>
    </Header>
  );
};
